// Power calculations for the site's tests (see power() below).
// Noncentral t CDF: Lenth (1989), Algorithm AS 243. Checked against scipy by
// spec/javascript/power_check.mjs (run: node spec/javascript/power_check.mjs).

const LN_SQRT_PI = 0.5723649429247001; // ln(√π)
const SQRT_2_OVER_PI = 0.7978845608028654;

// ln Γ(x), Lanczos approximation.
export function lgamma(x) {
	const g = 7;
	const c = [0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
		-176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7];
	if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x);
	x -= 1;
	let a = c[0];
	const t = x + g + 0.5;
	for (let i = 1; i < g + 2; i++) a += c[i] / (x + i);
	return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a);
}

// Continued fraction for the incomplete beta function (Numerical Recipes).
function betacf(x, a, b) {
	const MAXIT = 300, EPS = 3e-16, FPMIN = 1e-300;
	const qab = a + b, qap = a + 1, qam = a - 1;
	let c = 1, d = 1 - (qab * x) / qap;
	if (Math.abs(d) < FPMIN) d = FPMIN;
	d = 1 / d;
	let h = d;
	for (let m = 1; m <= MAXIT; m++) {
		const m2 = 2 * m;
		let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
		d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
		c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
		d = 1 / d; h *= d * c;
		aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
		d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
		c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
		d = 1 / d;
		const del = d * c;
		h *= del;
		if (Math.abs(del - 1) < EPS) break;
	}
	return h;
}

// Regularized incomplete beta I_x(a, b).
export function ibeta(x, a, b) {
	if (x <= 0) return 0;
	if (x >= 1) return 1;
	const bt = Math.exp(lgamma(a + b) - lgamma(a) - lgamma(b) + a * Math.log(x) + b * Math.log(1 - x));
	return x < (a + 1) / (a + b + 2) ? (bt * betacf(x, a, b)) / a : 1 - (bt * betacf(1 - x, b, a)) / b;
}

// Standard normal CDF (Cody-style erfc via a high-precision rational approximation).
export function normalCdf(z) {
	// W. J. Cody's erf approximation through the complementary error function.
	const t = 1 / (1 + 0.5 * Math.abs(z) / Math.SQRT2);
	const y = t * Math.exp(-(z * z) / 2 - 1.26551223 + t * (1.00002368 + t * (0.37409196 + t * (0.09678418 +
		t * (-0.18628806 + t * (0.27886807 + t * (-1.13520398 + t * (1.48851587 + t * (-0.82215223 + t * 0.17087277)))))))));
	const erfcHalf = y / 2; // P(Z > |z|)
	return z >= 0 ? 1 - erfcHalf : erfcHalf;
}

// Central t CDF.
export function tCdf(t, df) {
	const tail = 0.5 * ibeta(df / (df + t * t), df / 2, 0.5);
	return t >= 0 ? 1 - tail : tail;
}

// Central t quantile, by bisection on the CDF.
export function tQuantile(p, df) {
	let lo = -1000, hi = 1000;
	for (let i = 0; i < 70; i++) {
		const mid = (lo + hi) / 2;
		if (tCdf(mid, df) < p) lo = mid; else hi = mid;
	}
	return (lo + hi) / 2;
}

// Noncentral t CDF, P(T ≤ t | df, delta). AS 243.
export function nctCdf(t, df, delta) {
	const ITRMAX = 2000, ERRMAX = 1e-12;
	let tt = t, del = delta, negdel = false;
	if (t < 0) { negdel = true; tt = -t; del = -delta; }

	let tnc = 0;
	const x = (tt * tt) / (tt * tt + df);
	if (x > 0) {
		const lambda = del * del;
		let p = 0.5 * Math.exp(-0.5 * lambda);
		let q = SQRT_2_OVER_PI * p * del;
		let s = 0.5 - p;
		let a = 0.5;
		const b = 0.5 * df;
		const rxb = Math.pow(1 - x, b);
		const albeta = LN_SQRT_PI + lgamma(b) - lgamma(a + b);
		let xodd = ibeta(x, a, b);
		let godd = 2 * rxb * Math.exp(a * Math.log(x) - albeta);
		let xeven = 1 - rxb;
		let geven = b * x * rxb;
		tnc = p * xodd + q * xeven;
		let en = 1;
		let errbd;
		do {
			a += 1;
			xodd -= godd;
			xeven -= geven;
			godd *= (x * (a + b - 1)) / a;
			geven *= (x * (a + b - 0.5)) / (a + 0.5);
			p *= lambda / (2 * en);
			q *= lambda / (2 * en + 1);
			s -= p;
			en += 1;
			tnc += p * xodd + q * xeven;
			errbd = 2 * s * (xodd - godd);
		} while (errbd > ERRMAX && en <= ITRMAX);
	}
	tnc += normalCdf(-del);
	if (negdel) tnc = 1 - tnc;
	return Math.min(1, Math.max(0, tnc));
}

// Central F upper tail and quantile (bisection; df may be fractional).
export function fSf(f, d1, d2) { return ibeta(d2 / (d2 + d1 * f), d2 / 2, d1 / 2); }
export function fQuantile(p, d1, d2) {
	let lo = 0, hi = 1000;
	while (hi < 1e7 && 1 - fSf(hi, d1, d2) < p) hi *= 10;
	for (let i = 0; i < 90; i++) {
		const mid = (lo + hi) / 2;
		if (1 - fSf(mid, d1, d2) < p) lo = mid; else hi = mid;
	}
	return (lo + hi) / 2;
}

// Noncentral F CDF as a Poisson mixture of central F (beta) CDFs.
export function ncfCdf(f, d1, d2, lambda) {
	const x = (d1 * f) / (d1 * f + d2);
	const half = lambda / 2;
	let total = 0;
	let weight = Math.exp(-half);
	for (let j = 0; j < 1000; j++) {
		if (j > 0) weight *= half / j;
		total += weight * ibeta(x, d1 / 2 + j, d2 / 2);
		if (j > half && weight < 1e-14) break;
	}
	return Math.min(1, Math.max(0, total));
}

export function normalQuantile(p) {
	let lo = -40, hi = 40;
	for (let i = 0; i < 80; i++) {
		const mid = (lo + hi) / 2;
		if (normalCdf(mid) < p) lo = mid; else hi = mid;
	}
	return (lo + hi) / 2;
}

// Regularized lower incomplete gamma P(a, x) (Numerical Recipes: series, or continued fraction).
export function gammaP(a, x) {
	if (x <= 0) return 0;
	const lead = Math.exp(-x + a * Math.log(x) - lgamma(a));
	if (x < a + 1) {
		let term = 1 / a, sum = term;
		for (let n = 1; n < 1000; n++) {
			term *= x / (a + n);
			sum += term;
			if (Math.abs(term) < Math.abs(sum) * 1e-16) break;
		}
		return Math.min(1, sum * lead);
	}
	const FPMIN = 1e-300;
	let b = x + 1 - a, c = 1 / FPMIN, d = 1 / b, h = d;
	for (let i = 1; i < 1000; i++) {
		const an = -i * (i - a);
		b += 2;
		d = an * d + b; if (Math.abs(d) < FPMIN) d = FPMIN;
		c = b + an / c; if (Math.abs(c) < FPMIN) c = FPMIN;
		d = 1 / d;
		const del = d * c;
		h *= del;
		if (Math.abs(del - 1) < 1e-16) break;
	}
	return Math.max(0, 1 - lead * h);
}

export const chi2Cdf = (x, df) => gammaP(df / 2, x / 2);

// Central χ² quantile, by bisection on the CDF.
export function chi2Quantile(p, df) {
	let lo = 0, hi = 10000;
	for (let i = 0; i < 90; i++) {
		const mid = (lo + hi) / 2;
		if (chi2Cdf(mid, df) < p) lo = mid; else hi = mid;
	}
	return (lo + hi) / 2;
}

// Noncentral χ² CDF as a Poisson mixture of central χ² CDFs.
export function ncx2Cdf(x, df, lambda) {
	const half = lambda / 2;
	let total = 0;
	let weight = Math.exp(-half);
	for (let j = 0; j < 5000; j++) {
		if (j > 0) weight *= half / j;
		total += weight * chi2Cdf(x, df + 2 * j);
		if (j > half && weight < 1e-15) break;
	}
	return Math.min(1, Math.max(0, total));
}

// Rank tests (Mann-Whitney, Wilcoxon signed-rank, Kruskal-Wallis) never need more than
// n / 0.864 to match their t or F counterpart (Hodges & Lehmann, 1956), so their power is
// taken as the parametric test's at n × 0.864: a worst case, usually beaten.
export const RANK_EFFICIENCY = 0.864;

function tPower(df, ncp, alpha, sides) {
	if (sides === 1) return 1 - nctCdf(tQuantile(1 - alpha, df), df, ncp);
	const crit = tQuantile(1 - alpha / 2, df);
	return 1 - nctCdf(crit, df, ncp) + nctCdf(-crit, df, ncp);
}

// Power for each test the site covers. `es` is the effect size in the method's own
// units: Cohen's d (t-tests), Cohen's f (ANOVA), r (correlation), Cohen's h (two
// proportions), Cohen's w (chi-square) or Cohen's q (two correlations). `n` is the total sample
// for one-sample, paired, correlation, chi-square, regression and repeated-measures (within) designs,
// and the per-group (or per-cell) size otherwise.
//   one_sample_t, paired_t: noncentral t with df = n − 1 (paired uses d_z)
//   two_sample_t: noncentral t with df = 2n − 2, equal group sizes
//   anova_f: noncentral F with λ = f² · k · n
//   correlation_r: Fisher z approximation
//   spearman_rs: Fisher z for Spearman's r_s: the test of zero judged with SE 1/√(n − 3) (Bonett & Wright's,
//     2000, SE at r_s = 0), the alternative spread by their SE √((1 + r_s²/2)/(n − 3)). Within about a point of
//     simulated t-approximation power for r_s = .2–.6, n = 20–93 (content/tests/spearman-correlation/NOTES.md).
//   two_proportions: normal approximation, z = h · √(n / 2), equal group sizes
//   chi_square: noncentral χ² with λ = w² · n, df from `df`
//   mann_whitney, wilcoxon_signed_rank, kruskal_wallis: two_sample_t, paired_t and anova_f at n × 0.864
//   regression_f2: Cohen's f² for `predictors` tested out of `totalPredictors`, n = total N;
//     noncentral F with df (predictors, N − totalPredictors − 1) and λ = f² · N (G*Power manual §13–14)
//   factorial_f: Cohen's f for one effect with `effectDf` in a design of `cells` groups, n per cell;
//     noncentral F with df (effectDf, N − cells) and λ = f² · N (G*Power manual §11)
//   rm_anova_f: G*Power's "ANOVA: Repeated measures, within factors" with one group (Faul, Erdfelder,
//     Lang & Buchner, 2007, Table 3): Cohen's f for `levels` = m measurements of n people, correlation
//     `rho` among them, nonsphericity correction `epsilon`: λ = f² · N · m · ε / (1 − ρ),
//     df1 = (m − 1)ε, df2 = (N − 1)(m − 1)ε
//   rm_interaction_f: G*Power's "Repeated measures, within-between interaction" (same table): `groups`
//     = k groups of n, m measurements: λ = f² · N · m · ε / (1 − ρ), df1 = (k − 1)(m − 1)ε,
//     df2 = (N − k)(m − 1)ε, N = k · n
//   compare_correlations: G*Power's "z test: Correlation – inequality of two independent Pearson r's"
//     (manual §27.5): q = z₁ − z₂ (Fisher z), n in group 1 and round(n · `ratio`) in group 2;
//     z = q / √(1/(n₁ − 3) + 1/(n₂ − 3)) against the standard normal
//   bf_anova: Bayes factor design analysis for a balanced one-way design with the default JZS prior
//     (Rouder, Morey, Speckman & Province, 2012, Eq. 12): the chance, if Cohen's f is true, that
//     BF₁₀ exceeds `threshold`. See bfDesign().
//   bf_two_sample_t: the same for a Bayesian two-sample t-test, in Cohen's d with the JZS prior's default
//     r = √2/2 on δ. With two equal groups that Bayes factor is the ANOVA's at r = 0.5, and f = d/2, so it
//     runs as bf_anova with groups = 2 and es = d/2. See bfDesignFor().
//   one_proportion_exact: the exact binomial test of one proportion against `p0`. The effect is the true
//     proportion π (es), not a distance. See binomialPower(). Power is a sawtooth in n, so sampleSizeFor()
//     scans rather than bisects, and returns the n from which power stays at the target.
//   cluster_randomized: two-level design with a random intercept, n = J clusters of `clusterSize` people,
//     intraclass correlation `icc`. See clusterPower().
export function power({ method = "one_sample_t", es, d, n, alpha = 0.05, sides = 2, groups = 3, df = 1, predictors = 1, totalPredictors = 1, effectDf = 1, cells = 4,
	levels = 3, rho = 0.5, epsilon = 1, ratio = 1, threshold = 3, priorScale = 0.5, clusterSize = 20, icc = 0.1, predictor = "binary", level = 2, p0 = 0.5 }) {
	const effect = Math.abs(es ?? d);
	switch (method) {
		case "two_sample_t":
			return tPower(2 * n - 2, effect * Math.sqrt(n / 2), alpha, sides);
		case "anova_f": {
			const d1 = groups - 1, d2 = groups * (n - 1);
			return 1 - ncfCdf(fQuantile(1 - alpha, d1, d2), d1, d2, effect * effect * groups * n);
		}
		case "correlation_r":
			return zPower(Math.atanh(effect) * Math.sqrt(n - 3), alpha, sides);
		case "spearman_rs": {
			const spread = Math.sqrt(1 + effect * effect / 2); // alternative SE ÷ null SE
			const shift = Math.atanh(effect) * Math.sqrt(n - 3);
			if (sides === 1) return normalCdf((shift - normalQuantile(1 - alpha)) / spread);
			const z = normalQuantile(1 - alpha / 2);
			return normalCdf((shift - z) / spread) + normalCdf((-shift - z) / spread);
		}
		case "two_proportions":
			return zPower(effect * Math.sqrt(n / 2), alpha, sides);
		case "chi_square":
			return 1 - ncx2Cdf(chi2Quantile(1 - alpha, df), df, effect * effect * n);
		case "regression_f2": {
			const d2 = n - totalPredictors - 1;
			if (d2 < 1) return 0;
			return 1 - ncfCdf(fQuantile(1 - alpha, predictors, d2), predictors, d2, effect * n);
		}
		case "factorial_f": {
			const total = n * cells;
			return 1 - ncfCdf(fQuantile(1 - alpha, effectDf, total - cells), effectDf, total - cells, effect * effect * total);
		}
		case "rm_anova_f":
			return rmPower(effect, n, 1, levels, rho, epsilon, alpha, false);
		case "rm_interaction_f":
			return rmPower(effect, n * groups, groups, levels, rho, epsilon, alpha, true);
		case "compare_correlations": {
			const n2 = Math.round(n * ratio); // whole people in group 2
			if (n <= 3 || n2 <= 3) return 0;
			return zPower(effect / Math.sqrt(1 / (n - 3) + 1 / (n2 - 3)), alpha, sides);
		}
		case "bf_anova":
		case "bf_two_sample_t":
			return bfDesignFor({ method, n, groups, es: effect, threshold, priorScale }).h1Bf10;
		case "one_proportion_exact":
			return binomialPower(Math.round(n), es ?? d, p0, alpha, sides);
		case "cluster_randomized":
			return clusterPower(effect, n, clusterSize, icc, predictor, level, alpha, sides);
		case "mann_whitney":
			return power({ method: "two_sample_t", es: effect, n: n * RANK_EFFICIENCY, alpha, sides });
		case "wilcoxon_signed_rank":
			return power({ method: "paired_t", es: effect, n: n * RANK_EFFICIENCY, alpha, sides });
		case "kruskal_wallis":
			return power({ method: "anova_f", es: effect, n: n * RANK_EFFICIENCY, alpha, groups });
		default:
			return tPower(n - 1, effect * Math.sqrt(n), alpha, sides);
	}
}

// Faul et al. (2007), Table 3: within effects and within-between interactions. N people in k groups,
// m measurements each; ε multiplies λ and both df.
function rmPower(f, N, k, m, rho, eps, alpha, interaction) {
	const d1 = (interaction ? (k - 1) * (m - 1) : m - 1) * eps;
	const d2 = (N - k) * (m - 1) * eps;
	if (d2 <= 0 || rho >= 1) return 0;
	const lambda = (f * f * N * m * eps) / (1 - rho);
	return 1 - ncfCdf(fQuantile(1 - alpha, d1, d2), d1, d2, lambda);
}

// Two-level design, random intercept, J clusters of m people, intraclass correlation ρ. The effect is
// standardized on the outcome's total SD, √(τ + σ²), with ρ = τ / (τ + σ²), both among people with the
// same predictor value (for two conditions, the SD within a condition).
//   Cluster-level predictor (level 2): two conditions with J/2 clusters each, Cohen's d (Hedges & Rhoads,
//     2010, Eqs. 5–7; the design Raudenbush, 1997, analyzed, not read here): ncp = d · √(J·m / (4[1 + (m − 1)ρ])), df = J − 2. A continuous
//     cluster-level predictor, β per SD of the predictor across clusters, has Snijders's (2005, case 4)
//     sampling variance (mτ + σ²) / (J·m·s²): ncp = β · √(J·m / [1 + (m − 1)ρ]), the same with d = 2β.
//   Within-cluster predictor (level 1), centered on its cluster means and with no random slope: Snijders
//     (2005, case 2), variance σ² / (J·m·s²), so ncp = β · √(J·m / (1 − ρ)) (d: divide the J·m by 4), and
//     df = J(m − 1) − 1. A slope that varies across clusters lowers power (his case 3); this ignores it.
export function clusterDesign({ es, n: J, clusterSize: m = 20, icc = 0.1, predictor = "binary", level = 2 }) {
	const scale = predictor === "binary" ? 4 : 1;
	return level === 1
		? { df: J * (m - 1) - 1, ncp: es * Math.sqrt((J * m) / (scale * (1 - icc))) }
		: { df: J - 2, ncp: es * Math.sqrt((J * m) / (scale * (1 + (m - 1) * icc))) };
}

function clusterPower(es, J, m, icc, predictor, level, alpha, sides) {
	if (icc < 0 || icc >= 1) return 0;
	const { df, ncp } = clusterDesign({ es, n: J, clusterSize: m, icc, predictor, level });
	return df < 1 ? 0 : tPower(df, ncp, alpha, sides);
}

function zPower(shift, alpha, sides) {
	if (sides === 1) return normalCdf(shift - normalQuantile(1 - alpha));
	const z = normalQuantile(1 - alpha / 2);
	return normalCdf(shift - z) + normalCdf(-shift - z);
}

// ---- Exact binomial test of one proportion (one_proportion_exact) ----
// Power at n: the chance, if the true proportion is p1, of a count x whose exact p against p0 is at
// most alpha. Two-sided p is R's binom.test (and scipy's binomtest) rule: add up the probabilities,
// under p0, of every count no more likely than x (with R's 1 + 10⁻⁷ tolerance for ties). One-sided
// tests look in p1's direction.
// P(X = x) for x = 0…n: the mode's probability from ln Γ, then out from it by the ratio
// P(x + 1) / P(x) = (n − x)p / ((x + 1)(1 − p)), so far tails underflow to 0 harmlessly.
function binomialPmfs(n, p) {
	const f = new Float64Array(n + 1), odds = p / (1 - p);
	const mode = Math.min(n, Math.floor((n + 1) * p));
	f[mode] = Math.exp(lgamma(n + 1) - lgamma(mode + 1) - lgamma(n - mode + 1) + mode * Math.log(p) + (n - mode) * Math.log1p(-p));
	for (let x = mode; x < n; x++) f[x + 1] = f[x] * ((n - x) / (x + 1)) * odds;
	for (let x = mode; x > 0; x--) f[x - 1] = f[x] * (x / (n - x + 1)) / odds;
	return f;
}

export function binomialPower(n, p1, p0, alpha = 0.05, sides = 2) {
	if (!(n >= 1) || !(p1 > 0 && p1 < 1) || !(p0 > 0 && p0 < 1)) return 0;
	const f0 = binomialPmfs(n, p0);
	const f1 = binomialPmfs(n, p1);
	const reject = new Array(n + 1).fill(false);
	if (sides === 1) {
		const up = p1 > p0;
		let tail = 0;
		for (let i = 0; i <= n; i++) {
			const x = up ? n - i : i;
			tail += f0[x];
			if (tail > alpha * (1 + 1e-12)) break;
			reject[x] = true;
		}
	} else {
		// p(x) = Σ f0(y) over f0(y) ≤ f0(x)(1 + 10⁻⁷). The pmf rises to its mode and then falls, so
		// merging the two sides gives the probabilities in order; then prefix sums and a binary search.
		let mode = 0;
		for (let x = 1; x <= n; x++) if (f0[x] > f0[mode]) mode = x;
		const sorted = new Float64Array(n + 1);
		for (let i = 0, a = 0, b = n; i <= n; i++) sorted[i] = a <= mode && (b <= mode || f0[a] <= f0[b]) ? f0[a++] : f0[b--];
		const prefix = new Float64Array(n + 1);
		for (let i = 0, sum = 0; i <= n; i++) prefix[i] = sum += sorted[i];
		const pOf = (x) => {
			const limit = f0[x] * (1 + 1e-7);
			let lo = 0, hi = n + 1;
			while (lo < hi) { const mid = (lo + hi) >> 1; if (sorted[mid] <= limit) lo = mid + 1; else hi = mid; }
			return prefix[lo - 1];
		};
		// p rises toward the mode, so the significant counts are the two tails: walk in from each end.
		for (let x = 0; x <= mode && pOf(x) <= alpha * (1 + 1e-12); x++) reject[x] = true;
		for (let x = n; x > mode && pOf(x) <= alpha * (1 + 1e-12); x--) reject[x] = true;
	}
	let total = 0;
	for (let x = 0; x <= n; x++) if (reject[x]) total += f1[x];
	return total;
}

// Sample sizes for a sawtooth power curve: `first`, the smallest n that reaches the target, and
// `stable`, the n from which it never drops below again (checked to twice `first` plus 20, well past
// where the teeth stop crossing the target). Nulls when even maxN falls short.
export function sawtoothN(fn, target, lo = 2, maxN = 3000) {
	// The teeth are tiny by maxN, so a curve still well short there never reaches the target.
	if (fn(maxN) < target - 0.05) return { first: null, stable: null };
	let first = null;
	for (let n = lo; n <= maxN; n++) if (fn(n) >= target) { first = n; break; }
	if (first === null) return { first: null, stable: null };
	let stable = first;
	const end = Math.min(maxN, 2 * first + 20);
	for (let n = first + 1; n <= end; n++) if (fn(n) < target) stable = n + 1;
	return { first, stable: stable > maxN ? null : stable };
}

// ---- Bayes factor design analysis (bf_anova) ----
// The default (JZS) Bayes factor for a balanced one-way design with a groups of n (Rouder et al.,
// 2012, Eq. 12, p. 363), from the F statistic through R² = (a − 1)F / ((a − 1)F + N − a):
//   BF₁₀ = ∫ (1 + gn)^(−(a−1)/2) (1 − R² gn / (1 + gn))^(−(N−1)/2) π(g) dg,
//   π(g) = r / √(2π) · g^(−3/2) · exp(−r² / (2g)), the scaled inverse-χ² prior (r = 0.5, "medium").
// Integrated over u = ln g with Simpson's rule, as in app/lib/recompute/bayesian_anova.rb.
export function jzsLogBf10(F, n, a, r = 0.5) {
	const N = a * n;
	const r2 = ((a - 1) * F) / ((a - 1) * F + N - a);
	const lo = -25, hi = 20, intervals = 900, h = (hi - lo) / intervals;
	const logTerm = (u) => {
		const g = Math.exp(u), gn = g * n;
		const logPrior = Math.log(r / Math.sqrt(2 * Math.PI)) - 1.5 * u - (r * r) / (2 * g);
		const logLike = (-(a - 1) / 2) * Math.log1p(gn) - ((N - 1) / 2) * Math.log1p((-r2 * gn) / (1 + gn));
		return logLike + logPrior + u; // + u: dg = g du
	};
	const logs = [];
	for (let i = 0; i <= intervals; i++) logs.push(logTerm(lo + i * h));
	const top = Math.max(...logs);
	let total = 0;
	logs.forEach((v, i) => { total += (i === 0 || i === intervals ? 1 : i % 2 ? 4 : 2) * Math.exp(v - top); });
	return top + Math.log((total * h) / 3);
}

// F at which BF₁₀ equals `bf` (BF₁₀ rises with F). null if even F = 0 gives more than `bf`.
function fWhereBf10(bf, n, a, r) {
	const target = Math.log(bf);
	if (jzsLogBf10(0, n, a, r) >= target) return null;
	let lo = 0, hi = 1;
	while (jzsLogBf10(hi, n, a, r) < target && hi < 1e6) hi *= 2;
	for (let i = 0; i < 60; i++) {
		const mid = (lo + hi) / 2;
		if (jzsLogBf10(mid, n, a, r) < target) lo = mid; else hi = mid;
	}
	return (lo + hi) / 2;
}

// The Bayes factor depends on the data only through F, so the chance of each outcome follows from
// F's distribution: central F if there's no effect, noncentral F with λ = f² · N if Cohen's f is true.
// h1Bf10: P(BF₁₀ > threshold | f); h1Bf01: P(BF₀₁ > threshold | f) (misleading evidence);
// h0Bf01: P(BF₀₁ > threshold | no effect); h0Bf10: P(BF₁₀ > threshold | no effect).
export function bfDesign({ n, groups = 3, es = 0.25, threshold = 3, priorScale = 0.5 }) {
	const a = groups, N = a * n, d1 = a - 1, d2 = N - a;
	if (n < 2) return { h1Bf10: 0, h1Bf01: 0, h0Bf01: 0, h0Bf10: 0 };
	const f10 = fWhereBf10(threshold, n, a, priorScale);
	const f01 = fWhereBf10(1 / threshold, n, a, priorScale);
	const lambda = es * es * N;
	const below = (x, lam) => (x === null ? 0 : lam > 0 ? ncfCdf(x, d1, d2, lam) : 1 - fSf(x, d1, d2));
	return {
		h1Bf10: f10 === null ? 1 : 1 - below(f10, lambda),
		h1Bf01: below(f01, lambda),
		h0Bf01: below(f01, 0),
		h0Bf10: f10 === null ? 1 : fSf(f10, d1, d2),
		fBf10: f10, fBf01: f01
	};
}

// bfDesign for a calculator method: bf_two_sample_t takes d and two groups; bf_anova takes f and `groups`.
export function bfDesignFor({ method = "bf_anova", n, groups = 3, es = 0.25, threshold = 3, priorScale = 0.5 }) {
	if (method === "bf_two_sample_t") return bfDesign({ n, groups: 2, es: es / 2, threshold, priorScale: 0.5 });
	return bfDesign({ n, groups, es, threshold, priorScale });
}

// Smallest n the method can be computed at.
export function minN(args) {
	switch (args.method) {
		case "correlation_r": case "spearman_rs": return 4;
		case "chi_square": return (args.df || 1) + 1;
		case "regression_f2": return (args.totalPredictors || 1) + 2;
		case "compare_correlations": return Math.max(4, Math.ceil(3.5 / (args.ratio || 1))); // both groups need n > 3
		case "cluster_randomized": return clusterPairs(args) ? 4 : 3; // two conditions need two clusters each
		default: return 2;
	}
}

// Two conditions at the cluster level: clusters come in pairs, one per condition.
const clusterPairs = (args) => args.method === "cluster_randomized" && (args.predictor || "binary") === "binary" && (args.level ?? 2) === 2;

// Smallest n in [lo, maxN] at which fn(n) reaches target (null if it never does). fn rises with n,
// so this is a binary search.
export function smallestN(fn, target, lo = 2, maxN = 5000) {
	if (fn(maxN) < target) return null;
	let hi = maxN;
	while (lo < hi) {
		const mid = Math.floor((lo + hi) / 2);
		if (fn(mid) >= target) hi = mid; else lo = mid + 1;
	}
	return lo;
}

// Smallest n that reaches the target power (null if above maxN).
export function sampleSizeFor({ target = 0.8, maxN = 5000, ...args }) {
	if (!(args.es ?? args.d)) return null;
	if (args.method === "one_proportion_exact") {
		// Power is a sawtooth here: the n from which it stays at the target (see sawtoothN).
		if (Math.abs((args.es ?? args.d) - (args.p0 ?? 0.5)) < 0.005) return null;
		return sawtoothN((n) => power({ ...args, n }), target, minN(args), Math.min(maxN, 3000)).stable;
	}
	if (clusterPairs(args)) {
		const pairs = smallestN((k) => power({ ...args, n: 2 * k }), target, 2, Math.floor(maxN / 2));
		return pairs && 2 * pairs;
	}
	return smallestN((n) => power({ ...args, n }), target, minN(args), maxN);
}
